import CustomDrawer from "@/components/drawer";
import { FileRecord } from "@/models/club";
import {
  useAddGalleryImage,
  useDeleteGalleryImage,
  useGetAllGalleryImages,
} from "@/services/gallery/utilities";
import {
  ActionIcon,
  Box,
  Button,
  Grid,
  Group,
  LoadingOverlay,
  Modal,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconTrash } from "@tabler/icons-react";
import imageCompression from "browser-image-compression";
import { useState } from "react";
import ImageUploading, {
  ImageListType,
  ImageType,
} from "react-images-uploading";
import EditImageItem from "./EditImageItem";
import useIsSmall from "@/hooks/useIsSmall";

type Props = {
  opened: boolean;
  close: () => void;
};

type GalleryImage = ImageType & {
  serverId?: number; // Track if image is from server
  serverImageUrl?: string; // URL for server images
};

function EditGallery({ opened, close }: Props) {
  const [uploaderLoading, setUploaderLoading] = useState<boolean>();
  const [images, setImages] = useState<GalleryImage[] | []>([]);
  const isSmall = useIsSmall();
  const { data: serverImages } = useGetAllGalleryImages();
  const { mutate: addGalleryImage } = useAddGalleryImage();
  const { mutate: deleteGalleryImage, isLoading: deleteImageLoading } =
    useDeleteGalleryImage();

  const resizeImage = async (file: File) => {
    const options = {
      maxSizeMB: 0.5, // Compress to a maximum size
      maxWidthOrHeight: 1000, // Resize maintaining aspect ratio
      useWebWorker: true,
      fileType: file.type || "image/jpeg", // Preserve original file type
      initialQuality: 0.8, // Maintain good quality
    };

    try {
      const compressedFile = await imageCompression(file, options);
      return compressedFile;
    } catch (error) {
      console.error("Error resizing image:", error);
      throw error;
    }
  };

  const handleDeleteImage = async (id: number, index: number) => {
    // If it's a server image, delete it from server
    if (id) {
      deleteGalleryImage(id, {
        onSuccess: () => {
          notifications.show({
            message: "تصویر با موفقیت حذف شد",
            color: "green",
          });
          // Update local state to remove the image
          setImages(images.filter((_, i) => i !== index));
        },
        onError: () => {
          notifications.show({
            message: "خطا در حذف تصویر",
            color: "red",
          });
        },
      });
    } else {
      // Just remove from local state if it's a newly added image
      setImages(images.filter((_, i) => i !== index));
    }
  };

  const onChange = async (imageList: ImageListType) => {
    // This is an addition - compress the new image
    if (imageList.length > images.length) {
      setUploaderLoading(true);
      const newImage = imageList[imageList.length - 1];

      if (newImage?.file) {
        const originalFile = newImage.file;

        // Validate file type
        const allowedTypes = [
          "image/jpeg",
          "image/jpg",
          "image/png",
          "image/webp",
        ];
        if (!allowedTypes.includes(originalFile.type)) {
          notifications.show({
            message:
              "نوع فایل مجاز نیست. لطفاً تصویر با فرمت JPG، PNG یا WebP انتخاب کنید.",
            color: "red",
          });
          setUploaderLoading(false);
          return;
        }

        try {
          const compressedImage = await resizeImage(originalFile);
          // Preserve original file name and type for proper server validation
          const fileExtension = originalFile.name.split(".").pop() || "jpg";
          const fileName = `profile_image.${fileExtension}`;

          const imageFile = {
            ...newImage,
            file: new File([compressedImage], fileName, {
              type: originalFile.type || "image/jpeg",
            }),
          };

          addGalleryImage(imageFile?.file, {
            onSuccess: () => {
              notifications.show({
                message: "تصویر با موفقیت افزوده شد",
                color: "green",
              });
              // The query will auto-refetch and update serverImages
              setUploaderLoading(false);
            },
            onError: () => {
              notifications.show({
                message: "خطا در افزودن تصویر",
                color: "red",
              });
              setUploaderLoading(false);
            },
          });
        } catch (error) {
          notifications.show({
            message: "خطا در پردازش تصویر",
            color: "red",
          });
          setUploaderLoading(false);
        }
      }
    }
  };

  return (
    <>
      <Modal opened={opened && !isSmall} onClose={close} size="xl">
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
          mb="xl"
        >
          <Box
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box px="md">
              <Stack gap="md">
                <Title order={4}>تصاویر مجموعه را ویرایش کنید</Title>
                <Text size="xs" c="dimmed" ta="justify">
                  حداقل ۳ تصویر از موقعیت خود به پروفایل اضافه کنید. می‌توانید
                  تا ۱۰ تصویر بارگذاری کنید. در صورت تمایل می‌توانید بعداً
                  تصاویر بیشتری اضافه کرده یا تغییراتی ایجاد کنید.{" "}
                </Text>
              </Stack>
              <Stack mt="md">
                <ImageUploading
                  multiple
                  value={images || []}
                  onChange={onChange}
                  maxNumber={1}
                  dataURLKey="data_url"
                  acceptType={["jpg", "jpeg", "png", "webp"]}
                >
                  {({ onImageUpload, dragProps }) => (
                    <>
                      <Stack className="upload__image-wrapper">
                        <Group
                          onClick={onImageUpload}
                          justify="space-between"
                          p="md"
                          {...dragProps}
                          styles={{
                            root: {
                              borderRadius: "var(--mantine-radius-md)",
                              border: "1px dashed var(--mantine-color-gray-4)",
                            },
                          }}
                        >
                          <LoadingOverlay
                            visible={uploaderLoading || deleteImageLoading}
                          />
                          <Text>تصاویر خود را اضافه کنید</Text>
                          <Button variant="default" radius="xl" size="xs">
                            انتخاب تصویر
                          </Button>
                        </Group>
                      </Stack>
                      {serverImages && serverImages?.length > 0 && (
                        <Grid gutter="md">
                          {serverImages?.map((image, index) => (
                            <Grid.Col
                              key={image.id || `new-${index}`}
                              span={index === 0 ? 12 : 6}
                            >
                              <Stack pos="relative">
                                <ActionIcon
                                  variant="white"
                                  radius="xl"
                                  size="lg"
                                  color="gray.5"
                                  pos="absolute"
                                  right="10px"
                                  top="10px"
                                  onClick={() =>
                                    handleDeleteImage(image?.id || 0, index)
                                  }
                                  loading={deleteImageLoading}
                                >
                                  <IconTrash size={20} />
                                </ActionIcon>
                                <EditImageItem
                                  image={image?.image as FileRecord}
                                  h={
                                    index === 0
                                      ? isSmall
                                        ? 150
                                        : 250
                                      : isSmall
                                      ? 100
                                      : 200
                                  }
                                />
                              </Stack>
                            </Grid.Col>
                          ))}
                        </Grid>
                      )}
                    </>
                  )}
                </ImageUploading>
              </Stack>
            </Box>
          </Box>

          {/* <Card
          styles={{
            root: {
              position: "sticky",
              bottom: 0,
              zIndex: 10,
            },
          }}
          withBorder
          radius={0}
        >
          <Button
            type="submit"
            form="information"
            size="md"
            color="dark"
            fullWidth
          >
            ثبت تغییرات
          </Button>
        </Card> */}
        </Box>
      </Modal>
      <CustomDrawer
        opened={opened && isSmall}
        onClose={close}
        close={close}
        position="bottom"
        radius="md"
        size="xl"
        title=""
        custom="edit-merchant-gallery"
        styles={{
          body: {
            padding: 0,
          },
        }}
        zIndex={1000}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: "91svh",
          }}
        >
          <Box
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box px="md">
              <Stack gap="md">
                <Title order={4}>تصاویر مجموعه را ویرایش کنید</Title>
                <Text size="xs" c="dimmed" ta="justify">
                  حداقل ۳ تصویر از موقعیت خود به پروفایل اضافه کنید. می‌توانید
                  تا ۱۰ تصویر بارگذاری کنید. در صورت تمایل می‌توانید بعداً
                  تصاویر بیشتری اضافه کرده یا تغییراتی ایجاد کنید.{" "}
                </Text>
              </Stack>
              <Stack mt="md">
                <ImageUploading
                  multiple
                  value={images || []}
                  onChange={onChange}
                  maxNumber={1}
                  dataURLKey="data_url"
                  acceptType={["jpg", "jpeg", "png", "webp"]}
                >
                  {({ onImageUpload, dragProps }) => (
                    <>
                      <Stack className="upload__image-wrapper">
                        <Group
                          onClick={onImageUpload}
                          justify="space-between"
                          p="md"
                          {...dragProps}
                          styles={{
                            root: {
                              borderRadius: "var(--mantine-radius-md)",
                              border: "1px dashed var(--mantine-color-gray-4)",
                            },
                          }}
                        >
                          <LoadingOverlay
                            visible={uploaderLoading || deleteImageLoading}
                          />
                          <Text>تصاویر خود را اضافه کنید</Text>
                          <Button variant="default" radius="xl" size="xs">
                            انتخاب تصویر
                          </Button>
                        </Group>
                      </Stack>
                      {serverImages && serverImages?.length > 0 && (
                        <Grid gutter="md">
                          {serverImages?.map((image, index) => (
                            <Grid.Col
                              key={image.id || `new-${index}`}
                              span={index === 0 ? 12 : 6}
                            >
                              <Stack pos="relative">
                                <ActionIcon
                                  variant="white"
                                  radius="xl"
                                  size="lg"
                                  color="gray.5"
                                  pos="absolute"
                                  right="10px"
                                  top="10px"
                                  onClick={() =>
                                    handleDeleteImage(image?.id || 0, index)
                                  }
                                  loading={deleteImageLoading}
                                >
                                  <IconTrash size={20} />
                                </ActionIcon>
                                <EditImageItem
                                  image={image?.image as FileRecord}
                                  h={index === 0 ? 150 : 100}
                                />
                              </Stack>
                            </Grid.Col>
                          ))}
                        </Grid>
                      )}
                    </>
                  )}
                </ImageUploading>
              </Stack>
            </Box>
          </Box>

          {/* <Card
          styles={{
            root: {
              position: "sticky",
              bottom: 0,
              zIndex: 10,
            },
          }}
          withBorder
          radius={0}
        >
          <Button
            type="submit"
            form="information"
            size="md"
            color="dark"
            fullWidth
          >
            ثبت تغییرات
          </Button>
        </Card> */}
        </Box>
      </CustomDrawer>
    </>
  );
}

export default EditGallery;
